import { getEnvironment } from "config/api";
import { useGetPaymentProviderQuery } from "store/queries/finix";

import { usePatientOrderToken } from "./usePatientOrderToken";

/**
 * Single source of truth for "which payment rail does this order use".
 *
 * We need the authoritative per-order lookup (`GET /finix/orders/:id/payment-
 * provider`) whenever the order MIGHT be Finix-bound — to disambiguate a
 * Stripe-era order from a new Finix order under strict-freeze, AND to know
 * what `finixApplicationId` / `finixMerchantId` the form should render against.
 * Two signals tell us the lookup is needed:
 *
 *   1. The account's current `features.useFinix` flag — covers newly-created
 *      orders that haven't taken any payment yet (the flag picks the rail).
 *   2. The loaded order already has a FINIX payment on it — covers orders that
 *      took a Finix payment before the account was flipped back to Stripe.
 *      Without (2) the SPA would render the Stripe form for a Finix-bound
 *      order, and the backend's strict-freeze guard would reject the
 *      PaymentIntent with a 409 the user sees as "Payment Setup Error".
 *
 * A truly Stripe-only account (never flipped to Finix and no Finix payments on
 * the order) still skips the endpoint entirely, preserving the original
 * dependency-free path for those flows.
 *
 * For the pre-resolve window (still loading), `isFinix` falls back to either
 * signal so we don't briefly render the wrong form. Consumers that must not
 * render either form prematurely should also guard on `isLoading` / `isError`.
 */
export function useFinixPaymentProvider(orderInfo: any) {
  const accountFlag = !!orderInfo?.account?.features?.useFinix;
  const orderId = orderInfo?.id;
  const orderHasFinixPayment =
    Array.isArray(orderInfo?.payments) &&
    orderInfo.payments.some((p: any) => p?.paymentProvider === "FINIX");

  // Query when EITHER the account flag is on (new orders pick the rail by the
  // flag) OR the order already has a FINIX payment (strict-freeze binds it).
  const shouldQuery = !!orderId && (accountFlag || orderHasFinixPayment);

  // CLE-2181: this endpoint takes an order-bound patient token. It used to fire with whatever
  // `publicToken` happened to be in redux — a token for the PREVIOUSLY viewed order, or none at
  // all on a fresh load. The old API accepted any validly-signed token for any order so that
  // silently worked; it now returns 403 PATIENT_TOKEN_ORDER_MISMATCH (or 401 with no token,
  // which publicApi turns into a bounce to /login). Mint for THIS order and wait for it.
  const { isReady: isTokenReady, isPending: isTokenPending } =
    usePatientOrderToken(orderInfo);

  const {
    data: providerData,
    isLoading,
    isError,
  } = useGetPaymentProviderQuery(orderId, {
    skip: !shouldQuery || !isTokenReady,
  });

  const finixEnvironment: "sandbox" | "live" =
    getEnvironment() === "production" ? "live" : "sandbox";

  const isFinix = providerData
    ? providerData.provider === "FINIX"
    : accountFlag || orderHasFinixPayment;

  return {
    isFinix,
    // Gate loading on isTokenPending, NOT on !isTokenReady: an order that can never be
    // authenticated (no DOB on file) would otherwise leave consumers spinning forever. When the
    // token is unobtainable we settle and fall back to the account flag below, which is the
    // pre-CLE-2181 answer minus the unauthorized request.
    isLoading: shouldQuery ? isLoading || isTokenPending : false,
    isError: shouldQuery ? isError : false,
    finixApplicationId: providerData?.finixApplicationId,
    finixMerchantId: providerData?.finixMerchantId,
    finixEnvironment,
  };
}
